import { useState } from 'react';
import { toast } from 'react-toastify';
import useRequest from 'hooks/useRequest';
import { validatePrescriptionData } from '../services/prescriptionValidator';
import { scanPrescription, savePrescription } from '../services/prescriptionService';

/**
 * Hook for managing prescription scanning workflow
 */
const usePrescriptionScanning = (addToBasket, savePrescriptionInfo, setPrescriptionId, addPrescriptionProductId, navigate) => {
    const { sendRequest } = useRequest();
    const [prescriptionData, setPrescriptionData] = useState(null);
    const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [processingProducts, setProcessingProducts] = useState(false);
    const [prescriptionImageFile, setPrescriptionImageFile] = useState(null);

    const processPrescriptionResponse = (data) => {
        if (!data.products || data.products.length === 0) {
            toast.error('No products found in prescription');
            return false;
        }

        // Store the prescription data
        setPrescriptionData(data);

        // Initialize selected products with the first match from each product
        const initialSelection = data.products.map(product => {
            if (product.ids && product.ids.length > 0) {
                return {
                    ...product,
                    selectedId: product.ids[0][0], // First ID from first match
                    selectedName: product.ids[0][1] // First name from first match
                };
            }
            return product;
        });

        setSelectedProducts(initialSelection);
        setShowProductSelectionModal(true);
        return true;
    };

    const handlePrescriptionUpload = async (imageFile, setIsUploading, setPreviewImage) => {
        setIsUploading(true);
        try {
            // Store the image file for later use when saving prescription
            setPrescriptionImageFile(imageFile);

            // Create preview URL
            const previewUrl = URL.createObjectURL(imageFile);
            setPreviewImage(previewUrl);

            const response = await scanPrescription(imageFile, sendRequest);

            console.log('Prescription upload result:', response.data);
            if (response.status === 'success' && response.data) {
                // Validate prescription data quality
                const validation = validatePrescriptionData(response.data);
                console.log('Prescription validation:', validation);

                if (validation.isValid) {
                    // Good quality scan - proceed with processing
                    toast.success(validation.message);
                    processPrescriptionResponse(response.data);
                } else {
                    // Poor quality scan - show appropriate message
                    if (validation.quality === 'partial') {
                        toast.warning(validation.message);
                    } else {
                        toast.error(validation.message);
                    }

                    // Log details for debugging
                    if (validation.details) {
                        console.log('Validation details:', validation.details);
                    }

                    return false;
                }
            } else {
                toast.error('Failed to upload prescription. Please try again.');
                return false;
            }
        } catch (error) {
            console.error('Error processing prescription:', error);
            toast.error('Failed to process prescription. Please try again.');
            return false;
        } finally {
            setIsUploading(false);
        }
        return true;
    };

    const handleProductSelection = (productIndex, selectedMatch) => {
        const updatedProducts = [...selectedProducts];
        updatedProducts[productIndex] = {
            ...updatedProducts[productIndex],
            selectedId: selectedMatch[0],
            selectedName: selectedMatch[1]
        };
        setSelectedProducts(updatedProducts);
    };

    const handleConfirmProductSelection = async () => {
        setProcessingProducts(true);

        try {
            // Save prescription info to context and wait for it to complete
            if (prescriptionData) {
                await savePrescriptionInfo(prescriptionData);
            }

            // Add each selected product to basket
            let successCount = 0;
            let failCount = 0;

            for (const product of selectedProducts) {
                const basketProduct = {
                    integrationId: product.selectedId,
                    name: product.selectedName,
                    quantity: parseInt(product.op) || 1,
                    packaging: product.pck_size,
                    note: product.note,
                    sig: product.sig
                };

                const result = await addToBasket(basketProduct);
                if (result.status === 'success') {
                    successCount++;
                    // Track this product ID as added from prescription
                    if (product.selectedId && addPrescriptionProductId) {
                        addPrescriptionProductId(product.selectedId);
                    }
                } else {
                    failCount++;
                }
            }

            // Save prescription to backend
            if (prescriptionData?.scan_results) {
                try {
                    const prescriptionResponse = await savePrescription(
                        prescriptionData,
                        prescriptionImageFile,
                        sendRequest
                    );

                    console.log('Prescription save response:', prescriptionResponse);

                    // Show success toast if prescription was uploaded successfully
                    if (prescriptionResponse?.status === 'success') {
                        toast.success('Prescription uploaded successfully');

                        // Save the prescription ID from backend response
                        if (prescriptionResponse.data?.prescription?.id) {
                            setPrescriptionId(prescriptionResponse.data.prescription.id);
                        }

                        // Update prescription info with the image URL from backend
                        if (prescriptionResponse.data?.prescription?.photo) {
                            await savePrescriptionInfo(prescriptionData, prescriptionResponse.data.prescription.photo);
                        }
                    }
                } catch (prescriptionError) {
                    console.error('Error saving prescription:', prescriptionError);
                    // Continue with the flow even if prescription save fails
                }
            }

            if (successCount > 0) {
                toast.success(`Added ${successCount} product${successCount > 1 ? 's' : ''} to basket`);
                if (failCount > 0) {
                    toast.warning(`Failed to add ${failCount} product${failCount > 1 ? 's' : ''}`);
                }

                // Close modal and reset
                setShowProductSelectionModal(false);
                setPrescriptionData(null);
                setSelectedProducts([]);
                setPrescriptionImageFile(null);

                // Navigate to basket after ensuring state is saved
                setTimeout(() => {
                    navigate('/basket');
                }, 2000); // Increased delay to ensure localStorage is updated
            } else {
                toast.error('Failed to add products to basket');
            }
        } catch (error) {
            console.error('Error adding products to basket:', error);
            toast.error('Failed to add products to basket');
        } finally {
            setProcessingProducts(false);
        }
    };

    const handleCancelProductSelection = () => {
        setShowProductSelectionModal(false);
        setPrescriptionData(null);
        setSelectedProducts([]);
        setPrescriptionImageFile(null);
    };

    const resetPrescriptionState = () => {
        setPrescriptionData(null);
        setSelectedProducts([]);
        setPrescriptionImageFile(null);
        setShowProductSelectionModal(false);
        setProcessingProducts(false);
    };

    return {
        prescriptionData,
        showProductSelectionModal,
        selectedProducts,
        processingProducts,
        handlePrescriptionUpload,
        handleProductSelection,
        handleConfirmProductSelection,
        handleCancelProductSelection,
        resetPrescriptionState
    };
};

export default usePrescriptionScanning;
